import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, X } from 'lucide-react';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number;
}

interface ToastState extends ToastProps {
  id: number;
}

let toastId = 0;

export const Toast = ({
  message,
  type = 'success',
  id,
  onClose,
}: ToastState & { onClose: (id: number) => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, 3000);

    return () => clearTimeout(timer);
  }, [id, onClose]);

  const bgColor =
    type === 'error'
      ? 'bg-red-500'
      : type === 'success'
        ? 'bg-brand-midnight'
        : 'bg-brand-cobalt';
  const Icon = type === 'error' ? XCircle : CheckCircle;

  return (
    <div
      className={`${bgColor} flex min-w-64 items-center gap-2 rounded-xl px-4 py-3 text-white shadow-xl`}
    >
      <Icon className="h-5 w-5" />
      <span className="flex-1">{message}</span>
      <button
        onClick={() => onClose(id)}
        className="text-white hover:text-gray-200"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  useEffect(() => {
    const handleShowToast = (event: CustomEvent<ToastProps>) => {
      const newToast = {
        ...event.detail,
        id: ++toastId,
      };
      setToasts((prev) => [...prev, newToast]);
    };

    window.addEventListener('showToast', handleShowToast as EventListener);
    return () =>
      window.removeEventListener('showToast', handleShowToast as EventListener);
  }, []);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={removeToast} />
      ))}
    </div>
  );
};
